import type { IControlConfig, IControlState, INavigationStep } from "@/types/ComponentLibrary/component-library";
import type { IValidationErrorDto, IValidatorResult } from "@/types/FormEngine/validation";
import { defineComponent, type PropType } from "vue";
import FormInput from "@/library/templates/default/components/FormInput.vue";
import { controlStateStore } from "@/library/stores/ControlStateStore";
import { t, tRaw } from "@/services/Translation/t";
import { XmlHelper } from "@/helpers/Xml";

export default defineComponent({
  components: {
    FormInput: FormInput,
  },
  props: {
    identifier: { type: String },
    mtdt: {
      type: Object as PropType<ITreeField>,
      required: true,
    },
    level: { type: Number },
  },
  data() {
    let store = controlStateStore(this.identifier as string);
    let parent = window.FormEngine.form.sections.get(this.mtdt.Parent);

    let tableControl = null;
    if (this.mtdt.IsTableDetailElement) {
      tableControl = window.FormEngine.form.controls.get(this.mtdt.ParentTableControl);
    }
    if (!parent) {
      console.log("DOESN't HAVE A PARENT => ", this.identifier);
    }
    if (store.reload) {
      // XPATH RESOLUTIONS !
      //this.mtdt.XpathEdoc
      let normalizedCodeXpath = "";
      let xmlNodeCodeSelector = "";
      let xmlLastNodeCodeSelector = "";
      if (this.mtdt.XpathEdoc && this.mtdt.XmlNodeCodeSelector) {
        normalizedCodeXpath = this.mtdt.XpathEdoc;
        xmlNodeCodeSelector = this.mtdt.XmlNodeCodeSelector;
        xmlLastNodeCodeSelector = xmlNodeCodeSelector.split(" > ").pop() as string;
      }

      store.state = {
        isValid: true,
        errorMessages: new Array<IValidationErrorDto>(),
        hidden: this.mtdt.IsHidden === null ? false : this.mtdt.IsHidden,
        readOnly: this.mtdt.ReadOnlyFlag,
        hiddenLabel: this.mtdt.HasHiddenLabel === null ? false : (this.mtdt.HasHiddenLabel as boolean),
        hiddenHint: true,
        customMask: this.mtdt.CustomMask,
        value: this.mtdt.DefaultValue ? JSON.parse(JSON.stringify(this.mtdt.DefaultValue)) : "",
        silentSetValue: false,
        previousValue: null,
        displayValue: "",
        required: this.mtdt.IsRequired === null ? false : (this.mtdt.IsRequired as boolean),
        description: this.mtdt.Description,
        name: this.mtdt.Name,
        masked: this.mtdt.IsMasked,
        parent: parent as any,
        additional: {},
        parentTableControl: tableControl as any,
        classes: "",
        toExport: this.mtdt.XpathEdoc || this.mtdt.XpathText ? true : false,
      } as IControlState;
      if (this.mtdt.CustomMethods) {
        store.state.customMethods = this.mtdt.CustomMethods;
      }
      store.config = {
        normalizedCodeXpath: normalizedCodeXpath,
        xmlNodeCodeSelector: xmlNodeCodeSelector,
        xmlLastNodeCodeSelector: xmlLastNodeCodeSelector,
        connectedColumn: null,
        minLength: !this.mtdt.LDT.MinLength && this.mtdt.LDT.FieldType === "Autocomplete" ? 3 : this.mtdt.LDT.MinLength,
        maxLength: this.mtdt.LDT.MaxLength,
        autocompleteFunction: this.mtdt.LDT.AutocompleteFunction ? this.mtdt.LDT.AutocompleteFunction : null,
        editedParentRowNumber: null,
        requiredCondition: window.FormEngine.engineConfig.builder.requiredCondition
          ? window.FormEngine.engineConfig.builder.requiredCondition
          : function (self: any) {
            return !self.state.required;
          },
      } as IControlConfig;
    }
    store.reload = false;
    return {
      htmlElement: null as any,
      inputElement: null as any,
      store: store,
      defaultState: {
        isValid: true,
        hidden: this.mtdt.IsHidden,
        readOnly: this.mtdt.ReadOnlyFlag,
        hiddenLabel: this.mtdt.HasHiddenLabel === null ? false : (this.mtdt.HasHiddenLabel as boolean),
        hiddenHint: false,
        customMask: this.mtdt.CustomMask,
        value: this.mtdt.DefaultValue,
        displayValue: "",
        required: this.mtdt.IsRequired === null ? false : this.mtdt.IsRequired,
        description: this.mtdt.Description,
        name: this.mtdt.Name,
        masked: this.mtdt.IsMasked,
        parent: this.mtdt.Parent,
        classes: "",
      },
      state: store.state,
      config: store.config,
    };
  },
  created() {
    window.FormEngine.form.registerControl(this.identifier, this);
    //console.log("after register", this.identifier);
  },
  mounted() {
    if (this.identifier) {
      //console.log(this.identifier, " - Is mounted");
      this.htmlElement = document.getElementById(this.identifier);
      this.inputElement = document.getElementById(this.controlId);
    }
    //console.log(this.state.customMethods);
    if (this.state.customMethods) {
      //console.log(this.identifier, this.state.customMethods);
      for (let methodKey in this.state.customMethods) {
        this[methodKey as string] = this.state.customMethods[methodKey];
      }
    }
  },
  // unmounted() {
  //   if(this.mtdt.IsTableDetailElement)
  //   console.log(this.identifier, " - Is unmounted");
  // },
  // updated() {
  //   if(this.mtdt.IsTableDetailElement)
  //   console.log(this.identifier, " - is updating");
  // },
  computed: {
    additionalData(): any {
      return this.state.additional;
    },
    fillErrorBagComputed(): boolean {
      let fillErrorBag = true;
      if (
        this.mtdt.IsTableDetailElement &&
        this.state.parentTableControl &&
        this.state.parentTableControl.state.detailViewOpened &&
        this.config.editedParentRowNumber === null
      ) {
        fillErrorBag = false;
      }
      return fillErrorBag;
    },
    isValid(): boolean {
      return this.state.errorMessages.length === 0 ? true : false;
    },
    displayName(): string {
      return t("Name", this.identifier) + (this.displayRequiredCondition ? this.displayRequiredText : "");
    },
    displayHint(): string {
      return t("Hint", this.identifier);
    },
    displayHintText(): string {
      return this.state.hiddenHint ? t("displayHintTextClosed", "_utility") : t("displayHintTextOpened", "_utility");
    },
    displayHintClasses(): string {
      return "govuk-display-hint " + (this.state.hiddenHint ? "hint-closed" : "hint-opened");
    },
    displayTitle(): string {
      return t("Title", this.identifier);
    },
    displayRequiredText() {
      if (tRaw("_utility")) {
        return " " + t("requiredText", "_utility");
      } else {
        return " " + "(nepovinný údaj)";
      }
    },
    displayRequiredCondition() {
      return this.config.requiredCondition(this);
    },
    hasErrors() {
      return !this.state.isValid;
    },
    wrapperClasses() {
      let defaultSize = window.FormEngine?.engineConfig?.builder?.style?.controlWrapperDefaultInputSize || "lg";
      let classString = window.FormEngine.engineConfig.builder.style.controlWrapperClasses;
      if (this.state.required) {
        classString = classString + " " + window.FormEngine.engineConfig.builder.style.controlWrapperRequiredClass;
      }
      if (this.state.hidden) {
        classString = classString + " " + window.FormEngine.engineConfig.builder.style.controlWrapperHiddenClass;
      }
      if (!this.state.isValid) {
        classString = classString + " " + window.FormEngine.engineConfig.builder.style.controlWrapperErrorClass;
      }

      if (this.mtdt.LDT.Size !== defaultSize) {
        classString = classString + " size-" + this.mtdt.LDT.Size;
      }

      return classString;
    },
    controlDisabled() {
      return this.state.readOnly ? true : false;
    },
    hintId() {
      return this.identifier + "-hint";
    },
    titleId() {
      return this.identifier + "-title";
    },
    errorId() {
      return this.identifier + "-error";
    },
    currentErrors() {
      return this.state.errorMessages;
    },
    controlId() {
      return this.identifier + "-control";
    },
    isVisible() {
      var style = window.getComputedStyle(this.htmlElement);
      return !(style.display === "none" || style.visibility === "hidden");
      //return !(this.htmlElement.offsetParent === null);
    },
  },
  methods: {
    t,
    removeValidator(name: string) {
      window.FormEngine.form.validation.removeValidator(name, this.identifier);
    },

    // Event system helper functions
    subscribe(event: string, callback: Function, specifier?: string) {
      window.FormEngine.events.subscribe(event, callback, this.identifier, specifier, null);
    },
    toggleHintSection() {
      this.state.hiddenHint = !this.state.hiddenHint;
    },
    // State manipulation
    setRequired(required: boolean) {
      this.state.required = required;
      if (!required) {
        this.removeValidator("required");
        this.removeValidator("minLength");
        this.resetErrors();
      } else {
        window.FormEngine.form.validation.setValidatorFromLib("required", { control: this });
        if (this.mtdt.LDT.MinLength) {
          window.FormEngine.form.validation.setValidatorFromLib("minLength", { control: this });
        }
      }
    },
    setToExport(toExport: boolean) {
      this.state.toExport = toExport;
    },
    setValid(isValid: boolean) {
      this.state.isValid = isValid;
    },
    setErrors(errors: [IValidationErrorDto]) {
      let self = this;
      errors.forEach(function (item, index) {
        self.pushError(item);
      });
    },
    pushError(error: IValidationErrorDto) {
      let tempError = this.state.errorMessages.findIndex((validationErrorDto) => {
        return (
          validationErrorDto.targetId == error.targetId &&
          validationErrorDto.validatorGuid == error.validatorGuid &&
          validationErrorDto.navigationData == error.navigationData
        );
      });

      if (tempError === -1) {
        this.state.errorMessages.push(error);
      } else {
        this.state.errorMessages.splice(tempError, 1);
        this.state.errorMessages.push(error);
      }
      this.setValid(false);
    },
    removeError(validatorResult: IValidatorResult) {
      let errorIndex = this.state.errorMessages.findIndex(function (item) {
        return item.validatorGuid === validatorResult.validatorGuid && item.navigationData == validatorResult.navigationData && item.validatorGuid == validatorResult.validatorGuid;
      });
      if (errorIndex !== -1) {
        this.state.errorMessages.splice(errorIndex, 1);
        if (this.state.errorMessages.length === 0) {
          this.state.isValid = true;
        }
      }
    },
    resetErrors() {
      this.state.errorMessages = [];
      this.setValid(true);
      window.FormEngine.form.errorBag.removeErrors(this.identifier);
    },
    resetRequired() {
      this.setRequired(this.defaultState.required as boolean);
    },
    reset(silent: boolean = true) {
      //console.log("Reset Control => ", this.identifier);
      this.setValue(this.defaultState.value ? this.defaultState.value : "", silent);
      //this.config.editedParentRowNumber = null;
      //this.state.silentSetValue = false;
      // this.resetErrors();
    },
    handleXmlExport(parentElement: Element, topLevelXml: Element) {
      let selector = "";
      if (this.mtdt.IsTableDetailElement) {
        selector =
          this.state.parentTableControl.config.xmlLastNodeSelector +
          (this.config.xmlNodeCodeSelector as string).replace(
            this.state.parentTableControl.config?.xmlNodeSelector ? this.state.parentTableControl.config?.xmlNodeSelector : "",
            ""
          );
      } else {
        selector = this.config.xmlNodeCodeSelector as string;
      }
      let controlValue = this.getValue();
      if (selector !== "") {
        let eventObject = {
          control: this,
          selector: selector,
          value: controlValue,
          parentElement: parentElement,
          topLevelXml: topLevelXml
        };
        window.FormEngine.events.publish(
          window.FormEngine.events.list.BEFORE_EXPORT,
          eventObject,
          this.identifier
        );
        XmlHelper.setElementValue(parentElement, selector, eventObject.value);
        window.FormEngine.events.publish(
          window.FormEngine.events.list.AFTER_EXPORT,
          eventObject,
          this.identifier
        );
      }
    },
    handleXmlImport(parentXml: Element, silent: boolean) {
      let nodeCodeSelector = "";
      let eventObject = {
        control: this,
        silent: silent,
        xmlValue: "",
        parentXml: parentXml
      } as {
        control: any;
        silent: boolean;
        parentXml: Element;
        xmlValue: null | string;
      };
      if (this.mtdt.IsTableDetailElement) {
        if (this.config.xmlNodeCodeSelector) {
          let relativeXmlNodeCodeSelector = this.config.xmlNodeCodeSelector
            .split(this.state.parentTableControl.config.xmlLastNodeSelector + " > ")
            .pop() as string;
          let val = parentXml.querySelector(relativeXmlNodeCodeSelector);
          if (val) {
            eventObject.xmlValue = val.textContent;
            window.FormEngine.events.publish(
              window.FormEngine.events.list.BEFORE_IMPORT,
              eventObject,
              this.identifier
            );
            this.setValue(eventObject.xmlValue, silent);
            window.FormEngine.events.publish(
              window.FormEngine.events.list.AFTER_IMPORT,
              eventObject,
              this.identifier
            );
          }
        }
      } else {
        if (this.config.xmlNodeCodeSelector) {
          nodeCodeSelector = this.config.xmlNodeCodeSelector;
        }
        //console.log(this.config.xmlNodeCodeSelector,nodeCodeSelector);
        if (nodeCodeSelector) {
          let val = parentXml.querySelector(nodeCodeSelector);
          if (val) {
            eventObject.xmlValue = val.textContent;
            window.FormEngine.events.publish(
              window.FormEngine.events.list.BEFORE_IMPORT,
              eventObject,
              this.identifier
            );
            this.setValue(eventObject.xmlValue, silent);
            window.FormEngine.events.publish(
              window.FormEngine.events.list.AFTER_EXPORT,
              eventObject,
              this.identifier
            );
          }
        }
      }
    },
    getValue() {
      return this.state.value ? this.state.value : "";
    },
    setValue(value: any, silent: boolean = false, optionAttr: string = "Value") {
      this.state.silentSetValue = silent;
      this.store.state.value = typeof value == "number" ? value.toString() : value;
    },
    inputChanged(value: string) {
      this.state.value = value;
    },
    hide() {
      window.FormEngine.events.publish(
        window.FormEngine.events.list.HIDE,
        {
          validableId: this.identifier,
          control: this,
        },
        this.identifier
      );
      this.state.hidden = true;
      window.FormEngine.form.errorBag.removeErrors(this.identifier);
    },
    show() {
      window.FormEngine.events.publish(
        window.FormEngine.events.list.SHOW,
        {
          validableId: this.identifier,
          control: this,
        },
        this.identifier
      );
      this.state.hidden = false;
    },
    disable() {
      window.FormEngine.events.publish(
        window.FormEngine.events.list.DISABLE,
        {
          validableId: this.identifier,
          control: this,
        },
        this.identifier
      );
      this.state.readOnly = true;
    },
    enable() {
      window.FormEngine.events.publish(
        window.FormEngine.events.list.ENABLE,
        {
          validableId: this.identifier,
          control: this,
        },
        this.identifier
      );
      this.state.readOnly = false;
    },
    // Event functions
    focusOut() {
      this.validate(this.fillErrorBagComputed);
    },
    validate(fillErrorBag: boolean = true, fillControlErrors: boolean = true) {
      let validationDto = window.FormEngine.form.validation.newValidatorDto({}, this);
      let results = window.FormEngine.events.publish(window.FormEngine.events.list.VALIDATE, validationDto, this.identifier);
      let flaten = [].concat.apply([], results);
      //console.log("Base controls validation results => ", this.identifier, flaten);
      window.FormEngine.form.validation.resolveValidationResults(flaten, fillErrorBag, fillControlErrors);
      return flaten;
    },
    getNavigationData(): [INavigationStep] {
      if (!this.state.parentTableControl) {
        if (!this.state.parent) {
          console.log("Doesn't have a parent");
          return [
            {
              identifier: this.identifier,
              rowNumber: this.config.editedParentRowNumber,
            } as INavigationStep,
          ];
        }
        let navigationData = this.state.parent.getNavigationData();
        navigationData.push({
          identifier: this.identifier,
          rowNumber: this.config.editedParentRowNumber,
        } as INavigationStep);
        return JSON.parse(JSON.stringify(navigationData));
      } else {
        let navigationData = this.state.parentTableControl.getNavigationData();
        navigationData.push({
          identifier: this.identifier,
          rowNumber: this.config.editedParentRowNumber,
        } as INavigationStep);

        return JSON.parse(JSON.stringify(navigationData));
      }
    },
  },
});
